import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom"; // Import useParams
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import {
	FiFileText,
	FiUser,
	FiClock,
	FiMessageSquare,
	FiHash,
	FiList,
	FiGlobe,
} from "react-icons/fi";
import axios from "axios"; // If you need to fetch more data

const FileSummaryPage = () => {
	const location = useLocation();
	const { batch_id } = useParams(); // Get batch_id from URL params
	const [fileDetails, setFileDetails] = useState(location.state?.file || null);
	const [loading, setLoading] = useState(!fileDetails); // Set loading true if fileDetails not passed
	const [error, setError] = useState(null);

	useEffect(() => {
		// If fileDetails were not passed via state (e.g., direct access or refresh),
		// fetch them using the batch_id from the URL
		if (!fileDetails && batch_id) {
			const fetchFileById = async () => {
				setLoading(true);
				try {
					const response = await axios.get(
						`https://parallex.approot.ng/get_file_by_batch_id.php?batch_id=${batch_id}`
					);
					if (response.data.status && response.data.data) {
						setFileDetails(response.data.data); // Assuming data is the single file object
					} else {
						setError("File not found or an error occurred.");
					}
				} catch (err) {
					console.error("Error fetching file details:", err);
					setError("Failed to load file details.");
				} finally {
					setLoading(false);
				}
			};
			fetchFileById();
		} else if (!batch_id) {
			// Handle case where no batch_id is in URL and no state was passed
			setError("No file ID provided.");
			setLoading(false);
		} else {
			// If fileDetails were passed via state, we are not loading
			setLoading(false);
		}
	}, [fileDetails, batch_id]); // Dependency on fileDetails and batch_id

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "80vh",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ textAlign: "center", mt: 4, color: "error.main" }}>
				<Typography variant='h6'>{error}</Typography>
			</Box>
		);
	}

	if (!fileDetails) {
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<Typography variant='h6'>No file details available.</Typography>
				<Typography variant='body2'>
					Please navigate from the uploaded files table.
				</Typography>
			</Box>
		);
	}

	const fixedRatePerRecipient = 4.9;
	const averageRateComponents = [4.9, 4.9, 4.9, 2.7];
	const fromRatePerRecipient =
		averageRateComponents.reduce((sum, current) => sum + current, 0) /
		averageRateComponents.length;

	const estimatedCostFrom = fileDetails?.total_count
		? (fileDetails.total_count * fromRatePerRecipient).toFixed(2)
		: "N/A";
	const estimatedCostTo = fileDetails?.total_count
		? (fileDetails.total_count * fixedRatePerRecipient).toFixed(2)
		: "N/A";

	return (
		<Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
			<Typography
				variant='h4'
				gutterBottom
				component='h1'
				sx={{ fontWeight: "bold", color: "#1a237e" }}
			>
				<FiFileText style={{ verticalAlign: "middle", marginRight: "8px" }} />
				File Details: {fileDetails.file_name || "N/A"}
			</Typography>

			<Paper elevation={3} sx={{ p: 4, mt: 3, borderRadius: 2 }}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
						gap: 3,
					}}
				>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Batch ID:</span>{" "}
						{fileDetails.batch_id}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Uploader:</span>{" "}
						{fileDetails.full_name || "Unknown"}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Uploaded At:</span>{" "}
						{fileDetails.created_at}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Status:</span>{" "}
						{fileDetails.status}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Type:</span>{" "}
						{fileDetails.type || "N/A"}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Category:</span>{" "}
						{fileDetails.msg_cat || "N/A"}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Total Count:</span>{" "}
						{fileDetails.total_count}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Total Distinct:</span>{" "}
						{fileDetails.total_distinct}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Duplicates:</span>{" "}
						{fileDetails.duplicates}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Amount Sent:</span>{" "}
						{fileDetails.count}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Sender ID:</span>{" "}
						{fileDetails.senderid || "N/A"}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Scheduled For:</span>{" "}
						{fileDetails.schedule_time || "Immediate"}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Cost Center:</span>{" "}
						{fileDetails.cost_cntr || ""}
					</Typography>
					<Typography variant='body1'>
						<span style={{ fontWeight: "bold" }}>Estimated Cost:</span>{" "}
						{estimatedCostFrom} - {estimatedCostTo}
					</Typography>
				</Box>
				<Box sx={{ mt: 4, borderTop: "1px solid #e0e0e0", pt: 3 }}>
					<Typography variant='h6' gutterBottom>
						<FiMessageSquare
							style={{ verticalAlign: "middle", marginRight: "8px" }}
						/>
						Message Content:
					</Typography>
					<Typography
						variant='body1'
						sx={{
							whiteSpace: "pre-wrap",
							backgroundColor: "#f8f8f8",
							p: 2,
							borderRadius: 1,
						}}
					>
						{fileDetails.message || "No message content available."}
					</Typography>
				</Box>
				{/* You can add more details here as needed */}
			</Paper>
		</Box>
	);
};

export default FileSummaryPage;
